import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';

/** How many flagged commits to show before the "View more" toggle. */
const INITIAL_VISIBLE = 10;

/**
 * Right-hand review-queue rail, mirroring the Timeline page's snapshot-notes
 * column. Lists every commit the classifier flagged (top-2 confidence delta
 * < 0.15 on either axis), most-recent first, each linking to its per-commit
 * audit page. The full-width table is gone — triage now lives alongside the
 * Sankey + table instead of behind a tab.
 */
export function ReviewQueueRail() {
  const { data } = useAccountingView();
  const [expanded, setExpanded] = useState(false);

  const flagged = (data?.commits ?? [])
    .filter((c) => c.flagged_for_review)
    .sort((a, b) => b.committed_at - a.committed_at);

  const engineerById = new Map((data?.engineers ?? []).map((e) => [e.id, e]));
  const visible = expanded ? flagged : flagged.slice(0, INITIAL_VISIBLE);
  const hiddenCount = flagged.length - visible.length;

  return (
    <aside className={styles.reviewRail} aria-label='Review queue'>
      <div className={styles.reviewRailHead}>
        <span className={styles.reviewRailTitle}>Review queue</span>
        {flagged.length > 0 ? (
          <span className={styles.reviewRailCount}>{flagged.length}</span>
        ) : null}
      </div>

      {flagged.length === 0 ? (
        <p className={styles.reviewRailEmpty}>
          {data
            ? 'Nothing flagged — the classifier is confident on every commit this period.'
            : 'Loading…'}
        </p>
      ) : (
        <>
          <ul className={styles.reviewRailList}>
            {visible.map((c) => {
              const author = engineerById.get(c.author_engineer_id);
              return (
                <li key={c.sha} className={styles.reviewRailItem}>
                  <Link
                    to={`/accounting/commit/${c.sha}`}
                    className={styles.reviewRailSubject}
                  >
                    {c.subject}
                  </Link>
                  <div className={styles.reviewRailMeta}>
                    {author?.display_name ?? '—'}
                    {author?.kind === 'agent' ? ' 🤖' : ''} · {fmtRelative(c.committed_at, data!.period_end)}
                  </div>
                </li>
              );
            })}
          </ul>
          {hiddenCount > 0 ? (
            <button
              type='button'
              className={styles.reviewRailMore}
              onClick={() => setExpanded(true)}
            >
              View {hiddenCount} more
            </button>
          ) : expanded && flagged.length > INITIAL_VISIBLE ? (
            <button
              type='button'
              className={styles.reviewRailMore}
              onClick={() => setExpanded(false)}
            >
              Show less
            </button>
          ) : null}
        </>
      )}
    </aside>
  );
}

function fmtRelative(epoch: number, now: number): string {
  const days = Math.round((now - epoch) / (24 * 60 * 60));
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 14) return `${days} days ago`;
  return `${Math.round(days / 7)} weeks ago`;
}
